import { isEnvironmentCypressTest } from '@color/lib';
import Smooch from 'smooch';

import { IZendeskConversation } from '../types';
import { getZendeskIntegrationID } from '../utils';

import { MockSmooch } from './MockSmooch';

// Known anti-pattern below: App behavior should not be changing itself for tests. Needed here because Smooch is a 3rd party library.
// The Smooch SDK makes a lot of requests to Zendesk that are hard to mock because we do not have control over their internals.
// Instead of trying to guess what APIs need to return, we replace the entire SDK with a stub.
const smoochClient = isEnvironmentCypressTest() ? MockSmooch : Smooch;

/**
 * We defined wrappers around all of the `Smooch` functions primarily for testing
 * purposes. The Smooch SDK doesn't expose certain methods (namely getConversationById)
 * until the SDK has been initialized and rendered. That would require us to
 * either make an API call and render the widget or mock the API call, relying on the
 * specific internals of `init` method of the SDK. We needed to define this in a
 * separate file since you cannot easily mock a method that is defined in the same
 * module it's called in:
 * https://medium.com/welldone-software/jest-how-to-mock-a-function-call-inside-a-module-21c05c57a39f
 */
export const smoochGetConversationById = async (conversationId: string) => {
  return smoochClient.getConversationById(conversationId);
};

export const smoochInitQuery = async () => {
  return smoochClient.init({
    integrationId: getZendeskIntegrationID(),
    embedded: true,
  });
};

export const smoochLoginQuery = async (
  external_id: string,
  jwtToken: string,
  setIsLoading: (isLoading: boolean) => void
) => {
  return smoochClient.login(external_id, jwtToken).then(() => setIsLoading(false));
};

export const smoochGetConversations = async (): Promise<IZendeskConversation[]> => {
  const fetchedList = await Smooch.getConversations();
  /**
   * It's normally not standard practice to disable lint rules. This specific lint rule
   * guards against an application not taking full advantage of parallellization benefits
   * of async/await by using one Promise.all call (instead of await'ing each item in a loop).
   * However, in this case, the value of `Smooch.hasMoreConversations()` depends on each
   * successive call of `Smooch.getMoreConversations()`. This is more or less specifically called out in
   * the "When not to use it" section of the lint rule, namely when "the output of one iteration
   * may be used (or in this case, affect) the input to another".
   * */
  // eslint-disable-next-line no-await-in-loop
  while (await Smooch.hasMoreConversations()) {
    // eslint-disable-next-line no-await-in-loop
    const moreConversations = await Smooch.getMoreConversations();
    fetchedList.push(...moreConversations);
  }
  return fetchedList;
};

export const smoochSendMessageInConversation = async (message: string, conversationId: string) => {
  // We don't return here because we don't rely on this return value (the webSocket connection)
  // pulls the message rather than us pulling it off of the API return.
  return smoochClient.sendMessage(message, conversationId);
};
